import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";
import ContactFormLink from "@/components/ContactFormLink";

export default function UserConsent() {
  return (
    <LegalLayout slug="consent" eyebrow="Consent" title="User Consent" lastUpdated="May 26, 2026 · Reviewed by counsel">
      <p>
        When you create an account on Armstrong K9 Registry,
        you are asked to affirmatively consent to the following. This page
        summarises what that consent covers so you always have a reference.
      </p>

      <h2>What you consent to at signup</h2>
      <p>By checking the "I agree" box on the registration page, you confirm that:</p>
      <ul>
        <li>You are at least <strong>18 years of age</strong> and able to enter into a contract.</li>
        <li>You have read and accepted our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.</li>
        <li>You consent to the collection and processing of the personal information described in the Privacy Policy, for the purposes of operating the Registry.</li>
        <li>You consent to receive <strong>transactional</strong> emails from us (receipts, certificates, ownership-transfer invitations, security notices) at the email address on your account. These are not marketing emails and cannot be globally disabled while your account is active.</li>
        <li>You consent to the use of strictly-necessary cookies and browser storage that keep you logged in and remember your UI preferences.</li>
      </ul>

      <h2>What is optional</h2>
      <p>The following categories are <strong>off by default or togglable</strong> at any time in your Settings:</p>
      <ul>
        <li>Heat-cycle reminders for female dogs you own.</li>
        <li>Expiring-listing reminders for your breeding-directory entries.</li>
        <li>Occasional broadcast announcements from the Registry team.</li>
        <li>
          <strong>Web push notifications</strong> — when enabled, your
          browser may receive ~24-hour reminders before scheduled calendar
          events (heats, breedings, vet appointments, transport pickups,
          video tours) and real-time alerts for transport-status changes,
          escrow milestones, and new buyer inquiries. You enable them by
          accepting the browser's push permission prompt; you can revoke
          consent at any time from{" "}
          <Link to="/settings/notifications">Settings → Notifications</Link>{" "}
          or by removing the site from your browser's notification
          settings. Reminder delivery is best-effort and depends on your
          device, browser, and network — do not rely on push as the sole
          reminder for time-critical events.
        </li>
        <li>
          <strong>AI-assisted content generators</strong> — features
          marked "AI" or "AI-generated" (admin marketing-pack generator,
          kennel-bio assistant, etc.) are off by default and run only
          when you click them. See the{" "}
          <Link to="/privacy">Privacy Policy</Link> for what's sent to
          the AI provider and the{" "}
          <Link to="/terms">Terms of Service</Link> for the warranty
          disclaimer on AI output.
        </li>
      </ul>

      <h2>Consent for other members' data</h2>
      <p>
        When you record an ownership transfer, initiate a breeding enquiry,
        or send an in-app message, you confirm you have the right to share
        the counterparty's information with us for that purpose. You must
        <strong> not </strong>upload third-party contact information without
        a lawful basis.
      </p>

      <h2>Photograph &amp; likeness consent</h2>
      <p>
        You represent that you hold the copyright, or have obtained consent
        from the copyright holder, for every photograph of a dog, handler,
        or kennel that you upload. Photos may appear on public pedigree and
        breeder pages that anyone can view.
      </p>

      <h2>Withdrawing consent</h2>
      <p>
        You can withdraw consent at any time by:
      </p>
      <ul>
        <li>Disabling optional notifications in your <Link to="/settings">Settings</Link> page.</li>
        <li>Requesting account deletion via our <Link to="/contact">Contact Us</Link> page or{" "}
          <a href="mailto:privacy@armstrongk9registry.com">privacy@armstrongk9registry.com</a>
          <ContactFormLink topic="privacy" subject="Account deletion request" />.
        </li>
      </ul>
      <p>
        Withdrawal does not affect the lawfulness of processing performed
        before the withdrawal. Certain obligations (tax records, anti-fraud
        logs) require minimum retention periods.
      </p>

      <h2>Changes to consent language</h2>
      <p>
        Material changes to what you consent to — new categories of processing,
        new sub-processors handling sensitive categories — will be announced
        in your inbox and require renewed acceptance where required by law.
      </p>
    </LegalLayout>
  );
}
